import type { ComponentPublicInstance, MaybeRef, Ref } from 'vue'
import { computed, isRef, onMounted, ref, useAttrs } from 'vue'
import { useLoading } from '../../../composables/useLoading'

export type Option = { label: string; value: string }
export type SearchOption = Option & { search: string }

export type SelectProps = {
  options?: MaybeRef<Option[] | SearchOption[]>
  loadOptions?: (filterText: string) => Promise<Option[]>
  ensureSelected?: (selectedIds: string[]) => Promise<Option[]>
}

export function useSelect<T>(model: Ref<T>, props: SelectProps, rootElement: Ref<ComponentPublicInstance | null>) {
  const originalAttrs = useAttrs()
  const options = ref<Option[]>(getInitialOptions(props.options))
  const { load, loading } = buildLoader(props, options)

  if (props.ensureSelected && props.loadOptions) {
    const ensureSelected = props.ensureSelected
    onMounted(async () => {
      const ids = Array.isArray(model.value) ? model.value : model.value ? [model.value as string] : []
      if (ids.length > 0) {
        options.value = await ensureSelected(ids)
      }
    })
  }

  const localModel = computed<T>({
    get: () => model.value,
    set: (value: T) => {
      model.value = value
      setTimeout(() => {
        const el = rootElement.value?.$el as HTMLElement | undefined
        el?.dispatchEvent(new Event('change', { bubbles: true }))
      })
    }
  })

  const attrs = computed(() => ({
    ...originalAttrs,
    modelValue: localModel.value,
    'onUpdate:modelValue': (value: T) => (localModel.value = value),
    options: options.value,
    onBeforeShow: load,
    optionLabel: 'label',
    optionValue: 'value',
    showClear: true
  }))

  return { attrs, loading, loadOptions: load }
}

function getInitialOptions(options: MaybeRef<Option[] | SearchOption[]> | undefined): Option[] {
  return !options ? [] : isRef(options) ? options.value : options
}

function buildLoader(props: SelectProps, options: Ref<Option[]>) {
  if (props.loadOptions) {
    const loadOptions = props.loadOptions
    return useLoading(async (filterText: string | undefined) => {
      options.value = []
      options.value = await loadOptions(filterText ?? '')
    })
  }

  if (props.options) {
    const optionsRef = props.options
    return {
      load: (filterText: string | undefined) => {
        const source = isRef(optionsRef) ? optionsRef.value : optionsRef
        options.value =
          filterText && isSearchOptionArray(source) ? source.filter(x => x.search.includes(filterText)) : source
      },
      loading: false
    }
  }

  return { load: () => {}, loading: false }
}

function isSearchOptionArray(source: Option[] | SearchOption[]): source is SearchOption[] {
  const [first] = source
  return first !== undefined && 'search' in first
}
